import React, { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { useI18n } from '../contexts/I18nContext';

const GAP_MS = 2 * 60 * 60 * 1000; // break the line across gaps larger than ~2h.

// Build a chart series from sparse hourly buckets. Where two consecutive
// readings are more than ~2h apart, insert a null-valued point so Recharts
// (connectNulls=false) breaks the line instead of drawing across the gap.
function buildSeries(readings) {
  const sorted = [...readings]
    .filter((r) => r && r.recorded_at)
    .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at));
  const out = [];
  let prevTs = null;
  for (const r of sorted) {
    const ts = new Date(r.recorded_at).getTime();
    if (prevTs != null && ts - prevTs > GAP_MS) {
      out.push({ ts: prevTs + 1, temperature: null, humidity: null });
    }
    out.push({
      ts,
      temperature: typeof r.temperature === 'number' ? r.temperature : null,
      humidity: typeof r.humidity === 'number' ? r.humidity : null,
    });
    prevTs = ts;
  }
  return out;
}

const fmtHour = (ts) => new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const fmtFull = (ts) => new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

export function SensorHistoryChart({ readings, loading }) {
  const { t } = useI18n();
  const data = useMemo(() => buildSeries(Array.isArray(readings) ? readings : []), [readings]);
  const hasData = data.some((d) => d.temperature != null || d.humidity != null);

  if (loading) {
    return <div className="h-[280px] grid place-items-center text-ink-muted text-sm">{t('loading')}</div>;
  }
  if (!hasData) {
    return (
      <div className="h-[280px] grid place-items-center text-center px-6" data-testid="sensor-history-empty">
        <div>
          <div className="text-sm text-ink-soft">{t('sensor_history_empty_title')}</div>
          <div className="text-xs text-ink-muted mt-1">{t('sensor_history_empty_sub')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[280px]" data-testid="sensor-history-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" vertical={false} />
          <XAxis
            dataKey="ts" type="number" scale="time" domain={['dataMin', 'dataMax']}
            tickFormatter={fmtHour} stroke="#A0A8A2" fontSize={11} tickLine={false} axisLine={false} minTickGap={40}
          />
          <YAxis
            yAxisId="temp" orientation="left" stroke="#B36E5A" fontSize={11} tickLine={false} axisLine={false}
            width={38} tickFormatter={(v) => `${v}°`} domain={['auto', 'auto']}
          />
          <YAxis
            yAxisId="hum" orientation="right" stroke="#0284c7" fontSize={11} tickLine={false} axisLine={false}
            width={38} tickFormatter={(v) => `${v}%`} domain={[0, 100]}
          />
          <Tooltip
            labelFormatter={fmtFull}
            formatter={(value, name) => {
              if (value == null) return ['—', name];
              return name === 'temperature'
                ? [`${value.toFixed(1)} °C`, t('sensor_temperature')]
                : [`${value.toFixed(1)} %`, t('sensor_humidity')];
            }}
            contentStyle={{ borderRadius: 12, border: '1px solid #EAE6DF', fontSize: 12 }}
          />
          <Legend
            formatter={(v) => (v === 'temperature' ? t('sensor_temperature') : t('sensor_humidity'))}
            iconType="plainline" wrapperStyle={{ fontSize: 12 }}
          />
          <Line
            yAxisId="temp" type="monotone" dataKey="temperature" name="temperature"
            stroke="#B36E5A" strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false}
          />
          <Line
            yAxisId="hum" type="monotone" dataKey="humidity" name="humidity"
            stroke="#0284c7" strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
